import React, { useEffect, useMemo, useState } from 'react';
import { Job } from '../../store/jobSlice';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, StopOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Collapse, List, message, Popconfirm, Progress, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { JobStatus } from '../../constants/status';
import { cancelJob } from '../../api/cancelJob';


const { Panel } = Collapse;

interface JobListItemProps {
    job: Job;
}

const ACTIVE_JOB_STATUSES = [JobStatus.PENDING, JobStatus.IN_PROGRESS];

const toDayjs = (value?: { toDate?: () => Date } | Date | null) => {
    if (!value) return null;
    if (value instanceof Date) return dayjs(value);
    if (typeof value.toDate === 'function') return dayjs(value.toDate());
    return null;
};

const formatDuration = (milliseconds: number) => {
    if (!Number.isFinite(milliseconds) || milliseconds < 0) return '0s';

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const formatTimestamp = (value?: { toDate?: () => Date } | Date | null) => {
    const date = toDayjs(value);
    return date ? date.format('MMM D, YYYY HH:mm') : 'Unknown';
};

const JobListItem: React.FC<JobListItemProps> = ({ job }) => {
    const isActive = ACTIVE_JOB_STATUSES.includes(job.status);
    const [now, setNow] = useState(() => Date.now());
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        if (!isActive) return;

        const intervalId = window.setInterval(() => {
            setNow(Date.now());
        }, 15000);

        return () => window.clearInterval(intervalId);
    }, [isActive]);

    const getStatusIcon = (status: string) => {
        switch (status) {
            case JobStatus.PENDING:
                return <ClockCircleOutlined />;
            case JobStatus.IN_PROGRESS:
                return <SyncOutlined spin />;
            case JobStatus.COMPLETED:
                return <CheckCircleOutlined style={{ color: 'green' }} />;
            case JobStatus.FAILED:
                return <CloseCircleOutlined style={{ color: 'red' }} />;
            case JobStatus.PARTIAL_SUCCESS:
                return <ExclamationCircleOutlined style={{ color: 'orange' }} />;
            case JobStatus.CANCELLED:
                return <StopOutlined style={{ color: '#8c8c8c' }} />;
            default:
                return null;
        }
    };

    const getProgress = (job: Job) => {
        if (!job.count) return 0;
        const completed = job.success + job.failure + (job.cancelled ?? 0);
        return Math.min(Math.round((completed / job.count) * 100), 100);
    };

    const getProgressStatus = (job: Job) => {
        if (job.status === JobStatus.FAILED) return 'exception';
        if (job.status === JobStatus.COMPLETED) return 'success';
        if (job.status === JobStatus.IN_PROGRESS) return 'active';
        return 'normal';
    };

    const timing = useMemo(() => {
        const createdAt = toDayjs(job.createdAt);
        const startedAt = toDayjs(job.startedAt) ?? createdAt;
        const completedAt = toDayjs(job.completedAt);
        const cancelledAt = toDayjs(job.cancelledAt);
        const updatedAt = toDayjs(job.updatedAt);
        const endAt = isActive ? dayjs(now) : completedAt ?? updatedAt ?? dayjs(now);
        const completedItems = job.success + job.failure + (job.cancelled ?? 0);
        const remainingItems = Math.max((job.count || 0) - completedItems, 0);
        const elapsedMs = startedAt ? Math.max(endAt.diff(startedAt), 0) : 0;
        const canEstimate = isActive && completedItems >= 2 && remainingItems > 0;
        const estimatedMs = canEstimate ? (elapsedMs / completedItems) * remainingItems : null;

        return {
            elapsed: formatDuration(elapsedMs),
            eta: estimatedMs === null ? null : `~${formatDuration(estimatedMs)} left`,
            startedLabel: job.status === JobStatus.PENDING ? 'Queued' : 'Started',
            startedAt: formatTimestamp(job.status === JobStatus.PENDING ? job.createdAt : job.startedAt ?? job.createdAt),
            completedAt: completedAt ? formatTimestamp(job.completedAt) : null,
            cancelledAt: cancelledAt ? formatTimestamp(job.cancelledAt) : null,
            completedItems,
        };
    }, [job, isActive, now]);

    const headerTiming = isActive
        ? `Elapsed ${timing.elapsed}${timing.eta ? ` · ${timing.eta}` : ' · estimating time left'}`
        : `Duration ${timing.elapsed}`;

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await cancelJob({ companyId: job.companyId, jobId: job.id });
            message.success('Job cancelled');
        } catch (error) {
            console.error('Job cancellation failed:', error);
            message.error('Could not cancel job');
        } finally {
            setCancelling(false);
        }
    };

    return (
        <List.Item style={{ marginBottom: 16 }}>
            <Collapse bordered={false}>
                <Panel
                    header={
                        <Space direction='vertical' size="small" style={{ width: '100%' }}>

                            <Space size="small" style={{ display: 'flex', justifyContent: 'left', width: '100%' }}>
                                <Tooltip title={`Status: ${job.status}`}>
                                    {getStatusIcon(job.status)}
                                </Tooltip>
                                <Typography.Text strong>{job.name}</Typography.Text>

                            </Space>
                            <Progress
                                percent={getProgress(job)}
                                size="small"
                                status={getProgressStatus(job)}
                                style={{ width: "100%" }}
                            />
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {headerTiming}
                            </Typography.Text>
                        </Space>
                    }
                    key={job.id}
                >
                    <Space direction="vertical" size="small">
                        <Tag>{job.status}</Tag>
                        <Typography.Text type="secondary">
                            {timing.startedLabel}: {timing.startedAt}
                        </Typography.Text>
                        {timing.completedAt ? (
                            <Typography.Text type="secondary">
                                Completed: {timing.completedAt}
                            </Typography.Text>
                        ) : null}
                        {timing.cancelledAt ? (
                            <Typography.Text type="secondary">
                                Cancelled: {timing.cancelledAt}
                            </Typography.Text>
                        ) : null}
                        <Typography.Text type="secondary">
                            Elapsed: {timing.elapsed}
                        </Typography.Text>
                        {isActive ? (
                            <Typography.Text type="secondary">
                                Estimated time left: {timing.eta ?? 'Estimating after a few completed items'}
                            </Typography.Text>
                        ) : null}
                        <Typography.Text>
                            Done: {job.success} / Failed: {job.failure} / Cancelled: {job.cancelled ?? 0} / Total: {job.count}
                        </Typography.Text>
                        {job.lastError ? (
                            <Typography.Text type="secondary">
                                Last error: {job.lastError}
                            </Typography.Text>
                        ) : null}
                        {isActive ? (
                            <Popconfirm
                                title="Cancel this job?"
                                description="Queued work will stop before generation starts. Already-running work may finish."
                                okText="Cancel job"
                                okButtonProps={{ danger: true }}
                                cancelText="Keep running"
                                onConfirm={handleCancel}
                            >
                                <Button
                                    danger
                                    size="small"
                                    icon={<StopOutlined />}
                                    loading={cancelling}
                                >
                                    Cancel
                                </Button>
                            </Popconfirm>
                        ) : null}
                    </Space>
                </Panel>
            </Collapse>
        </List.Item>
    );
}

export default JobListItem;
